import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  FormHelperText,
  Typography,
} from "@mui/material";
import { ReactElement, useState } from "react";

import ContentColumnBox from "../../../components/Boxes/ContentColumnBox/ContentColumnBox";
import HorizontalBox from "../../../components/Boxes/HorizontalBox/HorizontalBox";
import TextField from "../../../components/TextField/TextField";
import { WireInfo } from "../../../utils/types/investor.type";
import { useWireEffect } from "./InvestorWire.hooks";

type Props = {
  useInvestorWireInput: any;
  useReceivingInput: any;
  useBeneficiaryInput: any;
  useIntermediaryInput: any;
  investorWireInfo: WireInfo;
  scrollToBottom: any;
};

const InvestorWire: React.FC<Props> = (props: Props): ReactElement => {
  const {
    useInvestorWireInput,
    useReceivingInput,
    useBeneficiaryInput,
    useIntermediaryInput,
    investorWireInfo,
    scrollToBottom,
  } = props;

  const { hideIntermediaryInfo, setHideIntermediaryBankInfo, setCanScroll } =
    useWireEffect(investorWireInfo, scrollToBottom);

  const onIntermediaryBankCheck = (event: any) => {
    setCanScroll(true);
    if (investorWireInfo.intermediaryBankInfo) {
      investorWireInfo.intermediaryBankInfo.abaNumber = '';
      investorWireInfo.intermediaryBankInfo.addressLine1 = '';
      investorWireInfo.intermediaryBankInfo.addressLine2 = '';
    }

    setHideIntermediaryBankInfo(!event.target.checked);
  };

  return (
    <Container id="investor_wire_container">
      <br />
      <HorizontalBox>
        <Typography variant="h4">Wire Instructions</Typography>
      </HorizontalBox>
      <HorizontalBox>
        <Typography variant="h3">Receiving Bank information</Typography>
      </HorizontalBox>
      <Box>
        <TextField
          size="small"
          id="txt_investor_Bank_ABA"
          label="Receiving Bank ABA/Swift"
          fullWidth
          {...useReceivingInput("abaNumber")}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          size="small"
          id="txt_receiving_bank_name"
          label="Receiving Bank Name"
          fullWidth
          {...useReceivingInput("name")}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          size="small"
          id="txt_Receiving_bank_Address1"
          label="Receiving Bank Address 1"
          fullWidth
          {...useReceivingInput("addressLine1")}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          size="small"
          id="txt_Receiving_bank_Address2"
          label="Receiving Bank Address 2"
          fullWidth
          {...useReceivingInput("addressLine2")}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Box>

      <br />

      <HorizontalBox noPadding>
        <Box>
          <HorizontalBox>
            <Typography variant="h3">Beneficiary Information</Typography>
          </HorizontalBox>

          <TextField
            size="small"
            id="txt_Beneficiary_Account"
            label="Beneficiary Account"
            {...useInvestorWireInput("beneficiaryAccount")}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            size="small"
            id="txt_Beneficiary_Name"
            label="Beneficiary Name"
            {...useInvestorWireInput("beneficiaryName")}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            size="small"
            id="txt_Beneficiary_Address_Line_1"
            label="Beneficiary Address Line 1"
            {...useInvestorWireInput("beneficiaryAddressLine1")}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            size="small"
            id="txt_Beneficiary_Address_Line_2"
            label="Beneficiary Address Line 2"
            {...useInvestorWireInput("beneficiaryAddressLine2")}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            size="small"
            id="txt_Beneficiary_bank_ABA"
            label="Beneficiary bank ABA/Swift"
            {...useBeneficiaryInput("abaNumber")}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            size="small"
            id="txt_Beneficiary_Bank_Name"
            label="Beneficiary Bank Name"
            {...useBeneficiaryInput("name")}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            size="small"
            id="txt_BankAddressLine1"
            label="Beneficiary Bank Address Line 1"
            {...useBeneficiaryInput("addressLine1")}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            size="small"
            id="txt_BankAddressLine2"
            label="Beneficiary Bank Address Line 2"
            {...useBeneficiaryInput("addressLine2")}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        <Box mt={4}>
          <HorizontalBox>
            <Typography variant="h3">Additional Instructions</Typography>
          </HorizontalBox>
          <TextField
            size="small"
            id="txt_originator_to_beneficiary"
            label="Originator to Beneficiary Instructions 1"
            rows={1}
            multiline
            inputProps={{ maxLength: 255 }}
            {...useInvestorWireInput("originatorToBeneficiaryInstructions1")}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            size="small"
            id="txt_originator_to_beneficiary"
            label="Originator to Beneficiary Instructions 2"
            rows={1}
            multiline
            inputProps={{ maxLength: 255 }}
            {...useInvestorWireInput("originatorToBeneficiaryInstructions2")}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            size="small"
            id="txt_originator_to_beneficiary"
            label="Originator to Beneficiary Instructions 3"
            rows={1}
            multiline
            inputProps={{ maxLength: 255 }}
            {...useInvestorWireInput("originatorToBeneficiaryInstructions3")}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            size="small"
            id="txt_originator_to_beneficiary"
            label="Originator to Beneficiary Instructions 4"
            rows={1}
            multiline
            inputProps={{ maxLength: 255 }}
            {...useInvestorWireInput("originatorToBeneficiaryInstructions4")}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <HorizontalBox sx={{ marginTop: 1 }}>
            <Typography variant="h3">Receiving Bank Information</Typography>
          </HorizontalBox>
          <TextField
            id="txt_bank_to_bank"
            label="Bank to Bank Instructions 1"
            rows={1}
            multiline
            inputProps={{ maxLength: 255 }}
            {...useInvestorWireInput("bankToBankInstructions1")}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            id="txt_bank_to_bank"
            label="Bank to Bank Instructions 2"
            rows={1}
            multiline
            inputProps={{ maxLength: 255 }}
            {...useInvestorWireInput("bankToBankInstructions2")}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            id="txt_bank_to_bank"
            label="Bank to Bank Instructions 3"
            rows={1}
            multiline
            inputProps={{ maxLength: 255 }}
            {...useInvestorWireInput("bankToBankInstructions3")}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            id="txt_bank_to_bank"
            label="Bank to Bank Instructions 4"
            rows={1}
            multiline
            inputProps={{ maxLength: 255 }}
            {...useInvestorWireInput("bankToBankInstructions4")}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      </HorizontalBox>

      <ContentColumnBox>
        <FormControlLabel
          control={<Checkbox />}
          id="txt_Bank_Account_not_in_US"
          label="Bank Account not in the US."
          checked={!hideIntermediaryInfo}
          value={hideIntermediaryInfo}
          onClick={onIntermediaryBankCheck}
        />
        <FormHelperText>
          Click here if your bank account is foreign to the US.
        </FormHelperText>
      </ContentColumnBox>
      <Box hidden={hideIntermediaryInfo}>
        <br />
        <Box>
          <TextField
            size="small"
            id="txt_investor_intermediary_bank_ABA"
            label="Intermediary Bank ABA/Swift"
            {...useIntermediaryInput("abaNumber")}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        <Box>
          <TextField
            size="small"
            id="txt_Receiving_intermediary_bank_Address1"
            label="Intermediary Bank Address 1"
            {...useIntermediaryInput("addressLine1")}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        <Box>
          <TextField
            size="small"
            id="txt_Receiving_intermediary_bank_Address2"
            label="Intermediary Bank Address 2"
            {...useIntermediaryInput("addressLine2")}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default InvestorWire;
